/**
 * CPU 合成（M2-03/M2-04 的显示与导出桥）。
 *
 * **这不是正式合成器。** 正式路径是 GPU Render Graph（M1-10~12），它做全部
 * 16 种混合模式与组语义。这里是应用层的朴素后备：normal 混合 + 不透明度 +
 * 可见性，服务两件事——把打开的图片显示到画布（M2-04）、把文档压平后导出
 * （M2-19 之前的基础导出）。当前阶段的文档来自导入图片与画笔，恰好只用到
 * 这些语义；GPU 显示管线接通后本模块退居导出兜底。
 *
 * 已知限制（都会在接入对应任务时消除，而不是悄悄留下）：
 * - 混合模式：PS 全部 27 种都支持颜色公式（可分离 REFERENCE_BLEND + 非可分离
 *   REFERENCE_NONSEPARABLE，均与 GPU 着色器同源）。唯一的例外是"溶解"的
 *   颗粒抖动——它的抖动作用在覆盖度上（见 `ditherNoise`），GPU 路径下没有
 *   对应实现（要新增像素坐标/随机种子这类跟颜色无关的 uniform，改动量明显
 *   大于其余模式），只有这条 CPU 桥会真的抖动，GPU 帧接管后会呈现成
 *   普通的 normal 混合（颜色正确，没有颗粒纹理）
 * - 忽略图层 transform（导入图片与画笔图层都是恒等变换）
 * - 不处理形状图层（跳过）；调整图层（曲线/色阶/HSL）自 M2-15 起支持
 * - 图层样式（投影/描边）自 M3-14 起支持，效果的模糊用同步盒式滤波近似
 *   高斯（见 engine/effects/render.ts 的文件头说明），非权威结果
 */
import type {
  AdjustmentLayer,
  BlendMode,
  Document,
  GroupLayer,
  Layer,
  LayerEffect,
  RasterLayer,
  Rect,
  SmartObjectLayer,
  TextLayer,
} from '../document/types';
import { NON_SEPARABLE_BLEND_MODES } from '../document/types';
import { findLayer } from '../document/draft';
import { TILE, type LayerId } from '../engine/tile/key';
import type { TileStore } from '../engine/tile/store';
import { applyResolved, resolveAdjustment, type ResolvedAdjustment } from '../engine/adjustments/resolve';
import { applyClarityAndDehaze } from '../engine/adjustments/basic';
import {
  EFFECT_ORDER,
  effectPhase,
  renderResolvedEffect,
  resolveEffect,
  type LayerEffectKind,
} from '../engine/effects/resolve';
import { renderBevelEmboss, renderStroke } from '../engine/effects/render';
import { patternCoverageAt } from '../engine/effects/patterns';
import { flattenPath } from '../engine/path/flatten';
import { subpathsToSelection } from '../engine/path/rasterize';
import { sampleVectorFill, type FillBounds, type VectorFillLike } from '../engine/fill/vector-fill';
import { REFERENCE_BLEND, type SeparableBlendMode } from '../gpu/shaders/blend/modes';
import { REFERENCE_NONSEPARABLE, type NonSeparableBlendMode } from '../gpu/shaders/blend/nonseparable';
import type { ShapeLayer } from '../document/types';
import { renderSmartFilterChain } from './smart-filter-render';

type Rgb = readonly [number, number, number];

/**
 * 一份可执行的 CPU 混合：`null` 是 normal/dissolve 的快捷路径（B(cb,cs)=cs，
 * 逐通道跟逐像素结果一样）；`separable` 逐通道调用；`whole` 要整个像素一起算
 * （深色/浅色/色相/饱和度/颜色/明度）。
 */
type CpuBlend =
  | null
  | { readonly kind: 'separable'; readonly fn: (cb: number, cs: number) => number }
  | { readonly kind: 'whole'; readonly fn: (cb: Rgb, cs: Rgb) => Rgb };

const NON_SEPARABLE_SET = new Set<string>(NON_SEPARABLE_BLEND_MODES);

/** 按混合模式选出要用的 CPU 参考实现；未识别的模式（不该发生）按 normal 处理。 */
function resolveCpuBlend(mode: BlendMode): CpuBlend {
  if (NON_SEPARABLE_SET.has(mode)) {
    return { kind: 'whole', fn: REFERENCE_NONSEPARABLE[mode as NonSeparableBlendMode] };
  }
  if (mode !== 'normal' && mode !== 'dissolve' && mode in REFERENCE_BLEND) {
    return { kind: 'separable', fn: REFERENCE_BLEND[mode as SeparableBlendMode] };
  }
  return null; // normal/dissolve 的逐通道颜色公式都是 cs，见 modes.ts 的说明
}

function applyCpuBlend(blend: CpuBlend, cb: Rgb, cs: Rgb): Rgb {
  if (blend === null) return cs;
  if (blend.kind === 'whole') return blend.fn(cb, cs);
  return [blend.fn(cb[0], cs[0]), blend.fn(cb[1], cs[1]), blend.fn(cb[2], cs[2])];
}

/**
 * "溶解"要用的稳定像素哈希（0–1）。不能用 `Math.random()`——同一图层每次
 * 重绘都要落在同一批像素上，否则画面会随便的重渲染而闪烁。整数哈希
 * （MurmurHash3 的定点混合步骤）纯函数、无状态，天然满足这一点。
 */
function ditherNoise(seed: number, x: number, y: number): number {
  let h = (seed ^ Math.imul(x, 374761393) ^ Math.imul(y, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}

/** 图层 id 的字符串哈希，当"溶解"抖动图案的种子——同一图层每次渲染种子不变。 */
function layerDitherSeed(layerId: string): number {
  let h = 0;
  for (let i = 0; i < layerId.length; i++) h = (Math.imul(h, 31) + layerId.charCodeAt(i)) >>> 0;
  return h;
}

export interface CompositeResult {
  readonly width: number;
  readonly height: number;
  /** RGBA8，直通 alpha（非预乘），行主序。 */
  readonly pixels: Uint8Array;
}

/** 把整个文档压平成一张 RGBA8 图。 */
export function compositeDocument(document: Document, tiles: TileStore): CompositeResult {
  const { width, height } = document.size;
  const pixels = new Uint8Array(width * height * 4);
  compositeRegion(document, tiles, { x: 0, y: 0, width, height }, pixels);
  return { width, height, pixels };
}

/**
 * 只合成给定的这几个图层 id（"合并图层"用，DESIGN.md §7.1 补充）——
 * 不是新写一套合成逻辑，是拿一份"根组子图层换成这几个"的临时文档去走
 * `compositeDocument` 原本那一套（混合模式/蒙版/效果全部照常生效，
 * 跟真实文档合成完全同一份代码路径，不会出现"单独合并时表现跟正常
 * 显示不一样"的不一致）。找不到的 id 静默忽略。
 */
export function compositeLayerSubset(
  document: Document,
  tiles: TileStore,
  memberIds: readonly LayerId[],
): CompositeResult {
  const members = memberIds
    .map((id) => findLayer(document.root, id)?.layer)
    .filter((layer): layer is Layer => layer !== undefined);
  const subsetRoot: GroupLayer = { ...document.root, children: members };
  return compositeDocument({ ...document, root: subsetRoot }, tiles);
}

/**
 * 单个图层在整个画布范围内的原始像素——不参与合成（没有不透明度/混合
 * 模式/蒙版/效果，就是这个图层自己画了什么），未画过的地方是透明黑。
 * 魔棒/快速选择"仅当前图层"取样用（真实 Photoshop 的默认值），
 * 跟 `compositeDocument` 的"全部可见图层"二选一。
 */
export function flattenLayerToImage(
  tiles: TileStore,
  layerId: LayerId,
  size: { readonly width: number; readonly height: number },
): CompositeResult {
  const bounds = { x: 0, y: 0, width: size.width, height: size.height };
  return { width: size.width, height: size.height, pixels: readLayerRegion(tiles, layerId, bounds) };
}

/**
 * 只合成文档空间里的一个矩形区域，结果写入 `out`（rect 尺寸的 RGBA8）。
 * 增量重绘的基本单元：脏了哪块就只算哪块（M2-04 的验收标准 2）。
 */
export function compositeRegion(
  document: Document,
  tiles: TileStore,
  rect: Rect,
  out: Uint8Array,
  skipLayerId?: string,
): void {
  out.fill(0);
  compositeGroup(document.root, 1, out, rect, tiles, skipLayerId);
}

function compositeGroup(
  group: GroupLayer,
  parentOpacity: number,
  out: Uint8Array,
  rect: Rect,
  tiles: TileStore,
  skipLayerId?: string,
): void {
  // children[0] 在最底下，依序向上合成
  // 剪贴蒙版（M2-13/M3-03）：clipped 图层的 alpha 与其下方最近的非剪贴
  // 基底图层的 alpha 相交（§4.4 第 7 步）
  let clipBase: RasterLayer | TextLayer | null = null;
  for (const layer of group.children) {
    if (!layer.visible) continue;
    // 显示期跳过（自由变换会话隐藏原图层，预览由 doc-overlay 画）
    if (layer.id === skipLayerId) continue;
    const opacity = parentOpacity * layer.opacity;
    if (layer.kind === 'group') {
      compositeGroup(layer, opacity, out, rect, tiles, skipLayerId);
      clipBase = null;
    } else if (layer.kind === 'raster' || layer.kind === 'text') {
      // 文本图层的像素由排版结果栅格化进 tile（M2-18），显示路径与 raster 相同
      compositeLayerEffects(layer, opacity, out, rect, tiles, 'below');
      compositeRaster(layer, opacity, out, rect, tiles, layer.clipped ? clipBase : null);
      compositeLayerEffects(layer, opacity, out, rect, tiles, 'above');
      if (!layer.clipped) clipBase = layer;
    } else if (layer.kind === 'adjustment') {
      compositeAdjustment(layer, opacity, out, rect, tiles);
      if (!layer.clipped) clipBase = null;
    } else if (layer.kind === 'shape') {
      compositeShapeLayerEffects(layer, opacity, out, rect, 'below');
      compositeShape(layer, opacity, out, rect);
      compositeShapeLayerEffects(layer, opacity, out, rect, 'above');
      clipBase = null; // 形状没有 alpha 供后续图层剪贴（简化：不作剪贴基底）
    } else if (layer.kind === 'smartObject') {
      compositeSmartObject(layer, opacity, out, rect, tiles);
      clipBase = null; // 简化：不作剪贴基底，与形状图层同一处理
    }
    // 其余图层种类见文件头的限制说明
  }
}

/** 剪贴基底在 (x, y) 的 alpha（0–255）。无基底 = 255（不裁剪）。 */
function baseAlphaAt(
  base: RasterLayer | TextLayer,
  tiles: TileStore,
  x: number,
  y: number,
): number {
  const tile = tiles.get({
    layer: base.id,
    tx: Math.floor(x / TILE),
    ty: Math.floor(y / TILE),
    lod: 0,
  });
  const data = tile?.data;
  if (data == null) return 0;
  return data[((y % TILE) * TILE + (x % TILE)) * 4 + 3]!;
}

/** 读蒙版值（0–255）。未分配块按 base（缺省 255=显示）。 */
function maskValueAt(
  layer: Layer,
  tiles: TileStore,
  x: number,
  y: number,
): number {
  const mask = layer.mask;
  if (mask === undefined || !mask.enabled) return 255;
  const tile = tiles.get({
    layer: mask.tiles.layer,
    tx: Math.floor(x / TILE),
    ty: Math.floor(y / TILE),
    lod: 0,
  });
  const data = tile?.data;
  if (data == null) return mask.base ?? 255;
  // 蒙版值存在 alpha 通道（写入侧的约定，见 app/commands/mask.ts）
  return data[((y % TILE) * TILE + (x % TILE)) * 4 + 3]!;
}

/**
 * 调整图层：作用于其下方**已合成**的结果（out 缓冲），受自身蒙版与
 * opacity 调制——蒙版 128 处效果恰好减半（M2-13/15 的共同语义）。
 */
function compositeAdjustment(
  layer: AdjustmentLayer,
  opacity: number,
  out: Uint8Array,
  rect: Rect,
  tiles: TileStore,
): void {
  const resolved = resolveAdjustment(layer.adjustment);
  if (resolved === null || opacity <= 0) return;
  const hasMask = layer.mask !== undefined && layer.mask.enabled;

  // 清晰度/去朦胧要看邻域，逐像素的 applyResolved 做不了——'basic' 且两者
  // 之一非零时，先在整个合成区域上跑一遍空间滤波，逐像素循环只负责取值+混合
  const spatial =
    resolved.type === 'basic' && (resolved.clarity !== 0 || resolved.dehaze !== 0)
      ? computeBasicSpatialResult(resolved, out, rect)
      : null;

  for (let y = 0; y < rect.height; y++) {
    for (let x = 0; x < rect.width; x++) {
      const at = (y * rect.width + x) * 4;
      if (out[at + 3] === 0) continue; // 透明处无色可调
      let factor = opacity;
      if (hasMask) {
        factor = (factor * maskValueAt(layer, tiles, rect.x + x, rect.y + y)) / 255;
      }
      if (factor <= 0) continue;
      let r: number, g: number, b: number;
      if (spatial !== null) {
        const si = (y * rect.width + x) * 3;
        r = spatial[si]!;
        g = spatial[si + 1]!;
        b = spatial[si + 2]!;
      } else {
        [r, g, b] = applyResolved(resolved, out[at]!, out[at + 1]!, out[at + 2]!);
      }
      out[at] = Math.round(out[at]! + (r - out[at]!) * factor);
      out[at + 1] = Math.round(out[at + 1]! + (g - out[at + 1]!) * factor);
      out[at + 2] = Math.round(out[at + 2]! + (b - out[at + 2]!) * factor);
    }
  }
}

/** 'basic' 调整的空间部分：先套逐通道 LUT（曝光~黑色），再清晰度/去朦胧。 */
function computeBasicSpatialResult(
  resolved: Extract<ResolvedAdjustment, { type: 'basic' }>,
  out: Uint8Array,
  rect: Rect,
): Uint8Array {
  const n = rect.width * rect.height;
  const rgb = new Uint8Array(n * 3);
  for (let i = 0; i < n; i++) {
    const at = i * 4;
    rgb[i * 3] = resolved.lut[out[at]!]!;
    rgb[i * 3 + 1] = resolved.lut[out[at + 1]!]!;
    rgb[i * 3 + 2] = resolved.lut[out[at + 2]!]!;
  }
  return applyClarityAndDehaze(rgb, rect.width, rect.height, resolved.clarity, resolved.dehaze);
}

/**
 * 图层内容在 rect 内的覆盖度（0–255，rect 局部坐标），已计入 opacity/fillOpacity/蒙版，
 * 但**不含**混合——效果渲染要的是"这块图层占了多少"，不是它跟背景混合后的样子。
 */
function sampleLayerAlpha(
  layer: RasterLayer | TextLayer,
  opacity: number,
  rect: Rect,
  tiles: TileStore,
): Uint8Array {
  const alphaScale = opacity * layer.fillOpacity;
  const hasMask = layer.mask !== undefined && layer.mask.enabled;
  const out = new Uint8Array(rect.width * rect.height);
  if (alphaScale <= 0) return out;

  for (const tile of tiles.residentTilesOf(layer.id, 0)) {
    const data = tile.data;
    if (data === null) continue;
    const originX = tile.key.tx * TILE;
    const originY = tile.key.ty * TILE;
    const left = Math.max(originX, rect.x);
    const top = Math.max(originY, rect.y);
    const right = Math.min(originX + TILE, rect.x + rect.width);
    const bottom = Math.min(originY + TILE, rect.y + rect.height);
    if (left >= right || top >= bottom) continue;

    for (let y = top; y < bottom; y++) {
      for (let x = left; x < right; x++) {
        const localIdx = ((y - originY) * TILE + (x - originX)) * 4 + 3;
        let a = (data[localIdx]! / 255) * alphaScale;
        if (hasMask && a > 0) a = (a * maskValueAt(layer, tiles, x, y)) / 255;
        out[(y - rect.y) * rect.width + (x - rect.x)] = Math.round(a * 255);
      }
    }
  }
  return out;
}

/** 单像素 source-over 混合（RGB，写回 out 的 alpha 通道也一并更新）。 */
function blendPixelSourceOver(out: Uint8Array, at: number, sa: number, r: number, g: number, b: number): void {
  if (sa <= 0) return;
  const da = out[at + 3]! / 255;
  const outA = sa + da * (1 - sa);
  if (outA > 0) {
    out[at] = Math.round((r * sa + out[at]! * da * (1 - sa)) / outA);
    out[at + 1] = Math.round((g * sa + out[at + 1]! * da * (1 - sa)) / outA);
    out[at + 2] = Math.round((b * sa + out[at + 2]! * da * (1 - sa)) / outA);
  }
  out[at + 3] = Math.round(outA * 255);
}

/** 用一份 alpha 遮罩 + 逐像素取色函数，把颜色混合进 `out`（rect 尺寸）。 */
function blendMaskedColorInto(
  out: Uint8Array,
  rect: Rect,
  alphaMask: Uint8Array,
  opacity: number,
  colorAt: (x: number, y: number) => readonly [number, number, number],
): void {
  for (let i = 0; i < alphaMask.length; i++) {
    const sa = (alphaMask[i]! / 255) * opacity;
    if (sa <= 0) continue;
    const x = i % rect.width;
    const y = Math.floor(i / rect.width);
    const [r, g, b] = colorAt(x, y);
    blendPixelSourceOver(out, i * 4, sa, r, g, b);
  }
}

/**
 * 把某一相位（"below"＝投影/外发光，画在图层内容下方；"above"＝其余
 * 全部）的图层效果合成进 out。同一相位内按 {@link EFFECT_ORDER} 的固定
 * 顺序渲染，不管 `layer.effects` 数组的原始顺序（见 resolve.ts 文件头）。
 *
 * `getLayerAlpha` 是惰性的：只有这个相位真的有匹配效果时才会被调用——
 * raster/text 图层算 `sampleLayerAlpha`（读 tile）、形状图层算
 * `sampleShapeLayerAlpha`（现算矢量几何）都不是免费的，没效果就不用算。
 */
function applyLayerEffects(
  effects: readonly LayerEffect[],
  getLayerAlpha: () => Uint8Array,
  out: Uint8Array,
  rect: Rect,
  phase: 'below' | 'above',
): void {
  const filtered = effects.filter((e) => effectPhase(e.kind as LayerEffectKind) === phase);
  if (filtered.length === 0) return;
  const ordered = [...filtered].sort(
    (a, b) => EFFECT_ORDER.indexOf(a.kind as LayerEffectKind) - EFFECT_ORDER.indexOf(b.kind as LayerEffectKind),
  );
  const layerAlpha = getLayerAlpha();

  for (const raw of ordered) {
    const resolved = resolveEffect(raw);
    if (resolved === null) continue;

    switch (resolved.kind) {
      case 'dropShadow':
      case 'innerShadow':
      case 'outerGlow':
      case 'innerGlow':
      case 'satin':
      case 'stroke': {
        const effectAlpha = renderResolvedEffect(resolved, layerAlpha, rect.width, rect.height);
        const color = resolved.color;
        blendMaskedColorInto(out, rect, effectAlpha, resolved.opacity, () => color);
        break;
      }
      case 'bevelEmboss': {
        const { highlight, shadow } = renderBevelEmboss(layerAlpha, rect.width, rect.height, resolved.params);
        const highlightColor = resolved.highlightColor;
        const shadowColor = resolved.shadowColor;
        blendMaskedColorInto(out, rect, highlight, resolved.opacity, () => highlightColor);
        blendMaskedColorInto(out, rect, shadow, resolved.opacity, () => shadowColor);
        break;
      }
      case 'colorOverlay': {
        const color = resolved.color;
        blendMaskedColorInto(out, rect, layerAlpha, resolved.opacity, () => color);
        break;
      }
      case 'gradientOverlay': {
        const { from, to, angle } = resolved;
        const rad = (angle * Math.PI) / 180;
        const dirX = Math.cos(rad);
        const dirY = Math.sin(rad);
        blendMaskedColorInto(out, rect, layerAlpha, resolved.opacity, (x, y) => {
          const t = Math.max(
            0,
            Math.min(1, ((x / rect.width) * dirX + (y / rect.height) * dirY + 1) / 2),
          );
          return [
            Math.round(from[0] + (to[0] - from[0]) * t),
            Math.round(from[1] + (to[1] - from[1]) * t),
            Math.round(from[2] + (to[2] - from[2]) * t),
          ];
        });
        break;
      }
      case 'patternOverlay': {
        const [r, g, b] = resolved.color;
        for (let i = 0; i < layerAlpha.length; i++) {
          const la = layerAlpha[i]!;
          if (la <= 0) continue;
          const x = i % rect.width;
          const y = Math.floor(i / rect.width);
          const coverage = patternCoverageAt(resolved.patternId, x, y, resolved.scale);
          if (coverage <= 0) continue;
          const sa = (la / 255) * coverage * resolved.opacity;
          blendPixelSourceOver(out, i * 4, sa, r, g, b);
        }
        break;
      }
    }
  }
}

/** raster/text 图层的效果入口：图层 alpha 从 tile 读（{@link sampleLayerAlpha}）。 */
function compositeLayerEffects(
  layer: RasterLayer | TextLayer,
  opacity: number,
  out: Uint8Array,
  rect: Rect,
  tiles: TileStore,
  phase: 'below' | 'above',
): void {
  applyLayerEffects(layer.effects, () => sampleLayerAlpha(layer, opacity, rect, tiles), out, rect, phase);
}

/** 形状图层的效果入口：图层 alpha 现算矢量几何（{@link sampleShapeLayerAlpha}）。 */
function compositeShapeLayerEffects(
  layer: ShapeLayer,
  opacity: number,
  out: Uint8Array,
  rect: Rect,
  phase: 'below' | 'above',
): void {
  applyLayerEffects(layer.effects, () => sampleShapeLayerAlpha(layer, opacity, rect), out, rect, phase);
}

/**
 * 把一份覆盖度 alpha（0–255，`maskRect` 尺寸）按矢量取色（纯色/渐变）混合进
 * `out`（`outRect` 尺寸）——只混合两者的交集，`maskRect`/`outRect` 各自的
 * 坐标原点可以不同（形状描边需要在比 `out` 大的范围里算形态学膨胀/腐蚀，
 * 见 compositeShape 的说明）。
 */
function blendVectorFill(
  fill: VectorFillLike,
  alpha: Uint8Array,
  alphaScale: number,
  fillBounds: FillBounds,
  maskRect: Rect,
  out: Uint8Array,
  outRect: Rect,
): void {
  const x0 = Math.max(maskRect.x, outRect.x);
  const y0 = Math.max(maskRect.y, outRect.y);
  const x1 = Math.min(maskRect.x + maskRect.width, outRect.x + outRect.width);
  const y1 = Math.min(maskRect.y + maskRect.height, outRect.y + outRect.height);
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const maskIndex = (y - maskRect.y) * maskRect.width + (x - maskRect.x);
      const [r, g, b, fillAlpha] = sampleVectorFill(fill, fillBounds, x - fillBounds.x, y - fillBounds.y);
      const sa = (alpha[maskIndex]! / 255) * alphaScale * fillAlpha;
      if (sa <= 0) continue;
      const at = ((y - outRect.y) * outRect.width + (x - outRect.x)) * 4;
      const da = out[at + 3]! / 255;
      const outA = sa + da * (1 - sa);
      if (outA > 0) {
        out[at] = Math.round((r * sa + out[at]! * da * (1 - sa)) / outA);
        out[at + 1] = Math.round((g * sa + out[at + 1]! * da * (1 - sa)) / outA);
        out[at + 2] = Math.round((b * sa + out[at + 2]! * da * (1 - sa)) / outA);
      }
      out[at + 3] = Math.round(outA * 255);
    }
  }
}

/**
 * 形状图层（M3-15/补充：描边+渐变）：路径栅格化（子扫描线覆盖度，与选区同口径）
 * 后按矢量取色（纯色/渐变）填充，描边（若有）用形态学环（同图层样式描边的
 * 膨胀/腐蚀算法）盖在填充上面——跟真实 Photoshop 形状图层的可视层次一致。
 * 路径坐标视为文档空间（忽略图层 transform，与本文件其余部分同一简化）。
 *
 * 掩码必须在图形自己的完整外接矩形（按描边宽度再扩一圈）上算，不能拿
 * `rect`（这次要合成的脏矩形，可能只是一块 tile）当画布边界——不然
 * `rect` 恰好整个落在图形内部时，erode/dilate 会把 `rect` 自己的边界
 * 误判成图形边缘，在图形内部凭空长出一圈假描边（真实 bug，靠
 * compositeRegion 传一个远小于图形、完全在内部的子矩形复现过）。
 */
interface ShapeMaskGeometry {
  readonly shapeRect: Rect;
  readonly maskAlpha: Uint8Array;
  readonly fillBounds: FillBounds;
  readonly strokeWidth: number;
}

/**
 * 形状图层的几何（外接矩形+栅格化后的填充遮罩），供 {@link compositeShape}
 * 和 {@link sampleShapeLayerAlpha}（图层样式效果要用的"这个形状盖住了哪些
 * 像素"）共用——两处都要先解出同一份"图形自己的完整外接矩形"，抽成一份
 * 避免两处各算一次、日后改了容易只改对一处。
 */
function computeShapeMaskGeometry(layer: ShapeLayer, rect: Rect): ShapeMaskGeometry | null {
  const subpathsDoc = flattenPath(layer.path.commands);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const sub of subpathsDoc) {
    for (const p of sub.points) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  if (!Number.isFinite(minX)) return null;

  const strokeWidth = layer.stroke !== undefined ? Math.max(0, layer.stroke.width) : 0;
  const pad = strokeWidth > 0 ? Math.ceil(strokeWidth / 2) + 1 : 0;
  const shapeRect: Rect = {
    x: Math.floor(minX) - pad,
    y: Math.floor(minY) - pad,
    width: Math.ceil(maxX - minX) + pad * 2,
    height: Math.ceil(maxY - minY) + pad * 2,
  };
  // 跟这次要合成的矩形没有交集，不用算
  if (
    shapeRect.x + shapeRect.width <= rect.x ||
    shapeRect.x >= rect.x + rect.width ||
    shapeRect.y + shapeRect.height <= rect.y ||
    shapeRect.y >= rect.y + rect.height
  ) {
    return null;
  }

  const subpaths = subpathsDoc.map((sub) => ({
    points: sub.points.map((p) => ({ x: p.x - shapeRect.x, y: p.y - shapeRect.y })),
  }));
  const mask = subpathsToSelection(shapeRect.width, shapeRect.height, subpaths, layer.path.fillRule);
  if (mask.isEmpty) return null;

  const maskAlpha = new Uint8Array(shapeRect.width * shapeRect.height);
  const row = new Uint8Array(shapeRect.width);
  for (let y = 0; y < shapeRect.height; y++) {
    mask.readRow(y, 0, shapeRect.width, row);
    maskAlpha.set(row, y * shapeRect.width);
  }

  // 渐变几何用图形自己紧贴的外接矩形（不含描边的扩边），跟属性栏"填充/
  // 描边"的视觉预期一致
  const fillBounds: FillBounds = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  return { shapeRect, maskAlpha, fillBounds, strokeWidth };
}

function compositeShape(layer: ShapeLayer, opacity: number, out: Uint8Array, rect: Rect): void {
  const alphaScale = opacity * layer.fillOpacity;
  if (alphaScale <= 0) return;
  const geometry = computeShapeMaskGeometry(layer, rect);
  if (geometry === null) return;
  const { shapeRect, maskAlpha, fillBounds, strokeWidth } = geometry;

  if (layer.fill !== undefined) {
    blendVectorFill(layer.fill, maskAlpha, alphaScale, fillBounds, shapeRect, out, rect);
  }
  if (strokeWidth > 0 && layer.stroke !== undefined) {
    const strokeAlpha = renderStroke(maskAlpha, shapeRect.width, shapeRect.height, {
      width: strokeWidth,
      position: 'center',
    });
    blendVectorFill(layer.stroke.fill, strokeAlpha, alphaScale, fillBounds, shapeRect, out, rect);
  }
}

/**
 * 形状图层给图层样式效果用的"覆盖度"（0–255，`rect` 尺寸）：填充和描边
 * 各自的矢量取色 alpha 通道（而不是颜色），按"填充在下、描边在上"的
 * source-over 顺序叠出一份联合覆盖度——效果（投影/发光…）要的是"这个
 * 图层本身画出了多少"，不是它跟背景混合后的样子，跟 raster/text 图层
 * 用 `sampleLayerAlpha` 是同一个语义，只是形状图层没有 tile 像素，得从
 * 矢量几何现算。
 */
function sampleShapeLayerAlpha(layer: ShapeLayer, opacity: number, rect: Rect): Uint8Array {
  const out = new Uint8Array(rect.width * rect.height);
  const alphaScale = opacity * layer.fillOpacity;
  if (alphaScale <= 0) return out;
  const geometry = computeShapeMaskGeometry(layer, rect);
  if (geometry === null) return out;
  const { shapeRect, maskAlpha, fillBounds, strokeWidth } = geometry;

  const accumulate = (maskBuf: Uint8Array, fill: VectorFillLike): void => {
    const x0 = Math.max(shapeRect.x, rect.x);
    const y0 = Math.max(shapeRect.y, rect.y);
    const x1 = Math.min(shapeRect.x + shapeRect.width, rect.x + rect.width);
    const y1 = Math.min(shapeRect.y + shapeRect.height, rect.y + rect.height);
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const maskIndex = (y - shapeRect.y) * shapeRect.width + (x - shapeRect.x);
        const m = maskBuf[maskIndex]! / 255;
        if (m <= 0) continue;
        const [, , , fillAlpha] = sampleVectorFill(fill, fillBounds, x - fillBounds.x, y - fillBounds.y);
        const a = m * alphaScale * fillAlpha;
        if (a <= 0) continue;
        const idx = (y - rect.y) * rect.width + (x - rect.x);
        const prev = out[idx]! / 255;
        out[idx] = Math.round((a + prev * (1 - a)) * 255);
      }
    }
  };

  if (layer.fill !== undefined) accumulate(maskAlpha, layer.fill);
  if (strokeWidth > 0 && layer.stroke !== undefined) {
    const strokeAlpha = renderStroke(maskAlpha, shapeRect.width, shapeRect.height, {
      width: strokeWidth,
      position: 'center',
    });
    accumulate(strokeAlpha, layer.stroke.fill);
  }
  return out;
}

/**
 * 把一个形状图层单独渲染成一张跟给定尺寸等大的 RGBA8——"栅格化图层"命令
 * （`app/rasterize.ts`）用，不参与合成、不受该图层自身 `opacity` 影响
 * （opacity 是普通图层属性，栅格化后仍然在合成阶段生效，跟原来的形状
 * 图层语义一致，这里只要它自己的填充/描边颜色，传 `opacity=1`）。
 */
export function renderShapeToImage(
  layer: ShapeLayer,
  size: { readonly width: number; readonly height: number },
): CompositeResult {
  const pixels = new Uint8Array(size.width * size.height * 4);
  compositeShape(layer, 1, pixels, { x: 0, y: 0, width: size.width, height: size.height });
  return { width: size.width, height: size.height, pixels };
}

function compositeRaster(
  layer: RasterLayer | TextLayer,
  opacity: number,
  out: Uint8Array,
  rect: Rect,
  tiles: TileStore,
  clipBase: RasterLayer | TextLayer | null = null,
): void {
  const alphaScale = opacity * layer.fillOpacity;
  if (alphaScale <= 0) return;
  const hasMask = layer.mask !== undefined && layer.mask.enabled;
  const blend = resolveCpuBlend(layer.blendMode);
  const isDissolve = layer.blendMode === 'dissolve';
  const ditherSeed = isDissolve ? layerDitherSeed(layer.id) : 0;

  for (const tile of tiles.residentTilesOf(layer.id, 0)) {
    const data = tile.data;
    if (data === null) continue;
    const originX = tile.key.tx * TILE;
    const originY = tile.key.ty * TILE;

    // 该块与目标区域的交集（文档空间）
    const left = Math.max(originX, rect.x);
    const top = Math.max(originY, rect.y);
    const right = Math.min(originX + TILE, rect.x + rect.width);
    const bottom = Math.min(originY + TILE, rect.y + rect.height);
    if (left >= right || top >= bottom) continue;

    for (let y = top; y < bottom; y++) {
      let src = ((y - originY) * TILE + (left - originX)) * 4;
      let dst = ((y - rect.y) * rect.width + (left - rect.x)) * 4;
      for (let x = left; x < right; x++) {
        let sa = (data[src + 3]! / 255) * alphaScale;
        if (hasMask && sa > 0) {
          sa = (sa * maskValueAt(layer, tiles, x, y)) / 255;
        }
        if (clipBase !== null && sa > 0) {
          sa = (sa * baseAlphaAt(clipBase, tiles, x, y)) / 255;
        }
        // "溶解"不是颜色公式，是覆盖度上的颗粒抖动：按当前有效覆盖度当阈值，
        // 这个像素要么整个不透明地画上去，要么完全不画，没有中间态
        if (isDissolve && sa > 0) {
          sa = ditherNoise(ditherSeed, x, y) < sa ? 1 : 0;
        }
        if (sa > 0) {
          // 直通 alpha 的 source-over；混合函数按 §4.4 的通用公式织入
          const da = out[dst + 3]! / 255;
          const outA = sa + da * (1 - sa);
          if (outA > 0) {
            const csRgb: Rgb = [data[src]! / 255, data[src + 1]! / 255, data[src + 2]! / 255];
            const cbRgb: Rgb = [out[dst]! / 255, out[dst + 1]! / 255, out[dst + 2]! / 255];
            const mixed = applyCpuBlend(blend, cbRgb, csRgb);
            for (let c = 0; c < 3; c++) {
              // rgb = (1-da)·sa·cs + da·sa·B(cb,cs) + (1-sa)·da·cb，再除 outA
              const rgb =
                ((1 - da) * sa * csRgb[c]! + da * sa * mixed[c]! + (1 - sa) * da * cbRgb[c]!) /
                outA;
              out[dst + c] = Math.round(rgb * 255);
            }
          }
          out[dst + 3] = Math.round(outA * 255);
        }
        src += 4;
        dst += 4;
      }
    }
  }
}

/** 读一个图层在 `bounds` 内的原始像素到一块紧凑缓冲（未画过的块留透明黑）。 */
function readLayerRegion(tiles: TileStore, layerId: LayerId, bounds: Rect): Uint8Array {
  const out = new Uint8Array(bounds.width * bounds.height * 4);
  for (const tile of tiles.residentTilesOf(layerId, 0)) {
    const data = tile.data;
    if (data === null) continue;
    const originX = tile.key.tx * TILE;
    const originY = tile.key.ty * TILE;
    const left = Math.max(originX, bounds.x);
    const top = Math.max(originY, bounds.y);
    const right = Math.min(originX + TILE, bounds.x + bounds.width);
    const bottom = Math.min(originY + TILE, bounds.y + bounds.height);
    if (left >= right || top >= bottom) continue;
    for (let y = top; y < bottom; y++) {
      const srcRowStart = ((y - originY) * TILE + (left - originX)) * 4;
      const dstRowStart = ((y - bounds.y) * bounds.width + (left - bounds.x)) * 4;
      out.set(data.subarray(srcRowStart, srcRowStart + (right - left) * 4), dstRowStart);
    }
  }
  return out;
}

/**
 * 智能对象：原始像素（`layer.raster`）→ 滤镜链（非破坏，每次重算，ADR-0008）
 * → 与其余图层同样的 source-over 混合写入 `out`。
 */
function compositeSmartObject(
  layer: SmartObjectLayer,
  opacity: number,
  out: Uint8Array,
  rect: Rect,
  tiles: TileStore,
): void {
  const alphaScale = opacity * layer.fillOpacity;
  if (alphaScale <= 0) return;
  const { bounds } = layer;

  const left = Math.max(bounds.x, rect.x);
  const top = Math.max(bounds.y, rect.y);
  const right = Math.min(bounds.x + bounds.width, rect.x + rect.width);
  const bottom = Math.min(bounds.y + bounds.height, rect.y + rect.height);
  if (left >= right || top >= bottom) return;

  const original = readLayerRegion(tiles, layer.raster.layer, bounds);
  const filtered = renderSmartFilterChain(original, bounds.width, bounds.height, layer.filters);
  const hasMask = layer.mask !== undefined && layer.mask.enabled;
  const blend = resolveCpuBlend(layer.blendMode);
  const isDissolve = layer.blendMode === 'dissolve';
  const ditherSeed = isDissolve ? layerDitherSeed(layer.id) : 0;

  for (let y = top; y < bottom; y++) {
    let src = ((y - bounds.y) * bounds.width + (left - bounds.x)) * 4;
    let dst = ((y - rect.y) * rect.width + (left - rect.x)) * 4;
    for (let x = left; x < right; x++) {
      let sa = (filtered[src + 3]! / 255) * alphaScale;
      if (hasMask && sa > 0) {
        sa = (sa * maskValueAt(layer, tiles, x, y)) / 255;
      }
      if (isDissolve && sa > 0) {
        sa = ditherNoise(ditherSeed, x, y) < sa ? 1 : 0;
      }
      if (sa > 0) {
        const da = out[dst + 3]! / 255;
        const outA = sa + da * (1 - sa);
        if (outA > 0) {
          const csRgb: Rgb = [filtered[src]! / 255, filtered[src + 1]! / 255, filtered[src + 2]! / 255];
          const cbRgb: Rgb = [out[dst]! / 255, out[dst + 1]! / 255, out[dst + 2]! / 255];
          const mixed = applyCpuBlend(blend, cbRgb, csRgb);
          for (let c = 0; c < 3; c++) {
            const rgb =
              ((1 - da) * sa * csRgb[c]! + da * sa * mixed[c]! + (1 - sa) * da * cbRgb[c]!) / outA;
            out[dst + c] = Math.round(rgb * 255);
          }
        }
        out[dst + 3] = Math.round(outA * 255);
      }
      src += 4;
      dst += 4;
    }
  }
}
